import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  useColorModeValue,
  useToast,
  VStack,
  HStack,
  Card,
  CardBody,
  InputGroup,
  InputLeftElement,
  FormHelperText,
  Badge,
  Divider,
  FormErrorMessage,
} from "@chakra-ui/react";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiLock,
  FiAward,
  FiBookOpen,
  FiUsers,
  FiArrowLeft,
  FiCheck
} from "react-icons/fi";
import Icon from "components/icons/Icon";
import { useAuth } from "contexts/AuthContext";
import { trainerService, CreateTrainerData } from "services/trainerService";
import * as authPb from '../../../generated/fitness-service/v1/auth_pb';
import { getAuthServicePromiseClient } from '../../../utils/grpcClient';

function TrainerSignup() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const { signup, isLoading, error: authError, clearError } = useAuth();
  
  // Custom signup function that doesn't store tokens
  const signupWithoutLogin = async (credentials: any) => {
    try {
      const request = new authPb.SignupRequest();
      request.setName(credentials.name);
      request.setEmail(credentials.email);
      request.setPassword(credentials.password);
      request.setRole(credentials.role);
      
      if (credentials.agencyCode) {
        request.setAgencyCode(credentials.agencyCode);
      }
      if (credentials.phone) {
        request.setPhone(credentials.phone);
      }
      if (credentials.specialization) {
        request.setSpecialization(credentials.specialization);
      }
      if (credentials.certification) {
        request.setCertification(credentials.certification);
      }
      if (credentials.experienceYears !== undefined) {
        request.setExperienceYears(credentials.experienceYears);
      }
      if (credentials.instagramHandle) {
        request.setInstagramHandle(credentials.instagramHandle);
      }

      const authServiceClient = getAuthServicePromiseClient();
      const response = await authServiceClient.signup(request, {});

      if (response) {
        // Don't store tokens - just return success
        return { success: true };
      } else {
        return {
          success: false,
          error: 'No response received from server'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Registration failed. Please try again.'
      };
    }
  };

  // Get agency code and role from URL parameters.
  // No agency code = independent trainer signing themselves up.
  const agencyCode = searchParams.get('agency_code');
  const role = searchParams.get('role');
  const isIndependent = !agencyCode;
  const isInvalidLink = role !== null && role !== 'trainer';

  // Form state - matching CreateTrainerData interface
  const [formData, setFormData] = useState<CreateTrainerData & { confirmPassword: string; instagramHandle: string }>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    specialization: '',
    certification: '',
    experienceYears: 0,
    instagramHandle: '',
  });
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  // Chakra color mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const bgColor = useColorModeValue('white', 'gray.800');

  // Redirect only if the link explicitly asks for a role we don't handle here
  useEffect(() => {
    if (isInvalidLink) {
      toast({
        title: "Invalid Signup Link",
        description: "This signup link is invalid or expired. Please contact the agency for a new link.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      navigate('/auth/sign-in');
    }
  }, [isInvalidLink, navigate, toast]);

  // Validation function - matching CreateTrainerModal logic
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    if (!formData.password.trim()) errors.password = 'Password is required';
    else if (formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (!formData.specialization.trim()) errors.specialization = 'Specialization is required';
    // An agency vets its trainers, so it gets the full profile. Solo trainers keep the friction low.
    if (!isIndependent) {
      if (!formData.phone.trim()) errors.phone = 'Phone is required';
      if (!formData.certification.trim()) errors.certification = 'Certification is required';
    }
    if (formData.experienceYears < 0) errors.experienceYears = 'Experience years must be positive';

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const processedValue = name === 'experienceYears' ? parseInt(value) || 0 : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear general errors
    if (error) setError('');
    if (authError) clearError();
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setError('');

    try {
      // ✅ Using custom signup function that doesn't store tokens
      const result = await signupWithoutLogin({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: 'trainer',
        agencyCode: agencyCode || '',
        // Trainer-specific fields:
        phone: formData.phone,
        specialization: formData.specialization,
        certification: formData.certification,
        experienceYears: formData.experienceYears,
        instagramHandle: formData.instagramHandle
      });

      if (result.success) {
        toast({
          title: "🎉 Account Created Successfully!",
          description: isIndependent
            ? `Welcome ${formData.name}! Your independent trainer account is active — sign in to get your trainer code and start adding clients.`
            : `Welcome ${formData.name}! Your complete trainer profile has been created with all your professional details and linked to the agency.`,
          status: "success",
          duration: 8000,
          isClosable: true,
        });

        // Redirect to trainer guide page
        setTimeout(() => {
          navigate('/auth/trainer-guide', {
            state: {
              trainerDetails: {
                name: formData.name,
                email: formData.email,
                agencyCode: agencyCode || 'Independent Trainer'
              }
            }
          });
        }, 2000);
      } else {
        const errorMsg = result.error || 'Registration failed. Please try again.';
        setError(errorMsg);

        toast({
          title: "Registration Failed",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      let errorMsg = 'An unexpected error occurred. Please try again.';

      if (err?.message) {
        errorMsg = err.message;
      } else if (typeof err === 'string') {
        errorMsg = err;
      }

      setError(errorMsg);

      toast({
        title: "Registration Error",
        description: errorMsg,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  // Don't render if invalid link
  if (isInvalidLink) {
    return null;
  }

  return (
    <Box
      minH="100vh"
      bg="gray.50"
      pt={{ base: "20px", md: "40px" }}
      pb={{ base: "40px", md: "60px" }}
      px={{ base: '20px', md: '40px' }}>

      <VStack spacing={8} maxW="600px" mx="auto">
        {/* Header */}
        <Box textAlign='center'>
          <Icon as={FiUsers} boxSize={16} color="brand.500" mb={4} />
          <Heading color={textColor} fontSize={{ base: '2xl', md: '3xl' }} mb={2}>
            {isIndependent ? 'Start Training Your Own Clients' : 'Join as a Trainer'}
          </Heading>
          <Text color={textColorSecondary} fontSize={{ base: 'sm', md: 'md' }}>
            {isIndependent
              ? 'For independent trainers and gym influencers — no agency required'
              : 'Complete your trainer registration'}
          </Text>

          {/* Agency / independent info */}
          <Card
            mt={6}
            bg={isIndependent ? 'orange.50' : 'blue.50'}
            border="1px solid"
            borderColor={isIndependent ? 'orange.200' : 'blue.200'}
          >
            <CardBody py={4}>
              <HStack justify="center" spacing={3}>
                <Icon as={FiCheck} color={isIndependent ? 'orange.500' : 'blue.500'} />
                <VStack spacing={0} align="start">
                  <Text fontSize="sm" fontWeight="600" color={isIndependent ? 'orange.700' : 'blue.700'}>
                    {isIndependent ? 'Independent Trainer' : `Agency: ${agencyCode}`}
                  </Text>
                  <Text fontSize="xs" color={isIndependent ? 'orange.600' : 'blue.600'}>
                    {isIndependent
                      ? 'Your account is active immediately, with your own trainer code to share'
                      : "You'll be automatically linked to this agency"}
                  </Text>
                </VStack>
              </HStack>
            </CardBody>
          </Card>
        </Box>

        {/* Main Form Card */}
        <Card w="100%" borderRadius="20px" boxShadow="xl" bg={bgColor}>
          <CardBody p={{ base: '30px', md: '40px' }}>
            {(error || authError) && (
              <Alert status="error" mb="20px" borderRadius="8px">
                <AlertIcon />
                {error || authError}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <VStack spacing={6} align="stretch">
                {/* Personal Information */}
                <Box>
                  <Text fontSize="lg" fontWeight="600" color={textColor} mb={4}>
                    Personal Information
                  </Text>
                  
                                    <VStack spacing={4}>
                    <HStack spacing={4} w="100%">
                      <FormControl isRequired isInvalid={!!fieldErrors.name} flex={1}>
                        <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                          Full Name<Text as="span" color={brandStars}>*</Text>
                        </FormLabel>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder='Enter your full name'
                          size='lg'
                          h='48px'
                        />
                        <FormErrorMessage>{fieldErrors.name}</FormErrorMessage>
                      </FormControl>

                      <FormControl isRequired isInvalid={!!fieldErrors.email} flex={1}>
                        <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                          Email Address<Text as="span" color={brandStars}>*</Text>
                        </FormLabel>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder='your@email.com'
                          size='lg'
                          h='48px'
                        />
                        <FormErrorMessage>{fieldErrors.email}</FormErrorMessage>
                      </FormControl>
                    </HStack>

                    <HStack spacing={4} w="100%">
                      <FormControl isRequired isInvalid={!!fieldErrors.password} flex={1}>
                        <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                          Password<Text as="span" color={brandStars}>*</Text>
                        </FormLabel>
                        <Input
                          name="password"
                          type="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder='Create a strong password'
                          size='lg'
                          h='48px'
                        />
                        <FormErrorMessage>{fieldErrors.password}</FormErrorMessage>
                      </FormControl>

                      <FormControl isRequired={!isIndependent} isInvalid={!!fieldErrors.phone} flex={1}>
                        <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                          Phone Number{!isIndependent && <Text as="span" color={brandStars}>*</Text>}
                        </FormLabel>
                        <Input
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder='+1 (555) 123-4567'
                          size='lg'
                          h='48px'
                        />
                        <FormErrorMessage>{fieldErrors.phone}</FormErrorMessage>
                      </FormControl>
                    </HStack>

                    <FormControl isRequired isInvalid={!!fieldErrors.confirmPassword}>
                      <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                        Confirm Password<Text as="span" color={brandStars}>*</Text>
                      </FormLabel>
                      <Input
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder='Confirm your password'
                        size='lg'
                        h='48px'
                      />
                      <FormErrorMessage>{fieldErrors.confirmPassword}</FormErrorMessage>
                    </FormControl>
                  </VStack>
                </Box>



                <Divider />

                {/* Professional Information */}
                <Divider />
                
                <VStack spacing={4}>
                  <HStack spacing={4} w="100%">
                    <FormControl isRequired isInvalid={!!fieldErrors.specialization} flex={1}>
                      <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                        Specialization<Text as="span" color={brandStars}>*</Text>
                      </FormLabel>
                      <Input
                        name="specialization"
                        value={formData.specialization}
                        onChange={handleInputChange}
                        placeholder='e.g., Weight Training, Cardio, Yoga'
                        size='lg'
                        h='48px'
                      />
                      <FormErrorMessage>{fieldErrors.specialization}</FormErrorMessage>
                    </FormControl>

                    <FormControl isRequired={!isIndependent} isInvalid={!!fieldErrors.certification} flex={1}>
                      <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                        Certification{!isIndependent && <Text as="span" color={brandStars}>*</Text>}
                      </FormLabel>
                      <Input
                        name="certification"
                        value={formData.certification}
                        onChange={handleInputChange}
                        placeholder='e.g., NASM-CPT, ACSM-CPT'
                        size='lg'
                        h='48px'
                      />
                      <FormErrorMessage>{fieldErrors.certification}</FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} w="100%">
                    <FormControl isInvalid={!!fieldErrors.experienceYears} flex={1}>
                      <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                        Years of Experience
                      </FormLabel>
                      <Input
                        name="experienceYears"
                        type="number"
                        value={formData.experienceYears}
                        onChange={handleInputChange}
                        placeholder='Enter years of experience'
                        size='lg'
                        h='48px'
                        min="0"
                        max="50"
                      />
                      <FormErrorMessage>{fieldErrors.experienceYears}</FormErrorMessage>
                    </FormControl>

                    <FormControl flex={1}>
                      <FormLabel color={textColor} fontSize='sm' fontWeight='500'>
                        Instagram Handle
                      </FormLabel>
                      <Input
                        name="instagramHandle"
                        value={formData.instagramHandle}
                        onChange={handleInputChange}
                        placeholder='yourhandle'
                        size='lg'
                        h='48px'
                      />
                      <FormHelperText fontSize="xs">
                        Optional — helps clients recognise you
                      </FormHelperText>
                    </FormControl>
                  </HStack>
                </VStack>

                {/* Info Alert */}
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="500">Complete Profile Creation</Text>
                    <Text fontSize="xs">
                      {isIndependent
                        ? "Your trainer profile is created and active straight away. Sign in to get the trainer code you share with clients — no agency or approval needed."
                        : "Your complete trainer profile will be created with all professional details and automatically linked to the agency. You'll be ready to start managing clients immediately after signing in."}
                    </Text>
                  </VStack>
                </Alert>

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  h="56px"
                  colorScheme="blue"
                  isLoading={isLoading}
                  loadingText="Creating Account..."
                  disabled={isLoading}
                  leftIcon={<Icon as={FiCheck} />}
                >
                  Create Trainer Account
                </Button>
              </VStack>
            </form>

            {/* Back to Sign In */}
            <VStack spacing={4} mt={8} pt={6} borderTop="1px solid" borderColor="gray.200">
              <Button
                leftIcon={<Icon as={FiArrowLeft} />}
                variant="ghost"
                onClick={() => navigate('/auth/sign-in')}
                color={textColorSecondary}
              >
                Back to Sign In
              </Button>

              <Text fontSize='xs' color={textColorSecondary} textAlign='center'>
                Already have an account?{' '}
                <Text
                  as="button"
                  color="brand.500"
                  fontWeight="500"
                  onClick={() => navigate('/auth/sign-in')}
                  _hover={{ textDecoration: 'underline' }}
                >
                  Sign In Here
                </Text>
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
}

export default TrainerSignup;